import React from "react";
import { useAuth } from "../auth/useAuth";
import { NavDropdown, OverlayTrigger, Tooltip } from "react-bootstrap";
import "./UserNav.scss";
import { processImagePath } from "../util/Functions";
import OrganizationMember from "../models/OrganizationMember";
import { DB } from "../util/Cache.util";

const InviteUserModal = React.lazy(() => import("../layouts/protected/pages/organizationAdmin/components/InviteUserModal"));
const ChangeLocationModal = React.lazy(() => import("../layouts/protected/pages/organizationAdmin/components/ChangeLocationModal"));
const SwitchAssociateModal = React.lazy(() => import("../layouts/private/components/associate/SwitchAssociateModal"));

export default function UserNav({ className }) {
	const { currentUser, logout, organizationMember } = useAuth();

	const [showInviteModal, setShowInviteAssociateModal] = React.useState(false);
	const [showLocationModal, setShowLocationModal] = React.useState(false);
	const [showChangeAssociate, setShowChangeAssociate] = React.useState(undefined);
	const [allowSwitchAssociate, setAllowSwitchAssociate] = React.useState(undefined);
	const dropdownRef = React.useRef(undefined);

	function open_location_modal() {
		setShowLocationModal(true);
	}

	const relocate = React.useCallback(dest => {
		window.location.href = dest;
	}, []);

	const organization_member_data = JSON.parse(localStorage.getItem('organization_member:data')) || organizationMember;
	const organization = organization_member_data?.organization;
	const features = organization?.features?.catalog?.permissions || {};

	const dropdown_click = React.useCallback(async e => {
		const isOpenBeforeClick = dropdownRef.current.classList.contains("show");

		// check if allowed to access associate list
		if (!isOpenBeforeClick && allowSwitchAssociate == null && features?.switch_associate !== false) {
			try {
				await OrganizationMember.FetchBy({
					filter: [{ property: "id", value: 0 }],
				})
				setAllowSwitchAssociate(true);
			}
			catch (e) {
				/* don't have permissions to switch associate */
				setAllowSwitchAssociate(false);
			}
		}
	}, [allowSwitchAssociate]);

	const updateSoftware = async () => {
		await DB.RemoveAll();
		// forceGet param in reload() non-standard and may not function as expected ... but it also may
		window.location.reload(true);
	};

	return (
		<NavDropdown
			ref={dropdownRef}
			onClick={dropdown_click}
			className={[className, "user-nav"].filter(c => c).join(" ")}
			title={
				<OverlayTrigger placement="bottom" overlay={
					<Tooltip className="_boss tooltip">Associate</Tooltip>
				}>
					<div data-component="UserNav">
						{currentUser?.picture
							? (<img src={processImagePath(currentUser?.picture)} alt="Profile" />)
							: (<>{(currentUser?.given_name ?? currentUser?.family_name ?? "?").at(0).toUpperCase()}</>)
						}
					</div>
				</OverlayTrigger>
			}
		>
			<NavDropdown.Item onClick={() => relocate("/user/profile")}>
				{currentUser?.username}
			</NavDropdown.Item>

			<ManageOrgNavItem member={organization_member_data} relocate={relocate} />

			{showChangeAssociate && (
				<React.Suspense fallback={<></>}>
					<SwitchAssociateModal onClose={() => setShowChangeAssociate(false)} />
				</React.Suspense>
			)}

			{organization_member_data?.organization?.id && (
				<>
					<NavDropdown.Item onClick={() => setShowInviteAssociateModal(true)}>
						Invite Team Members
					</NavDropdown.Item>
					{/* <NavDropdown.Item onClick={() => setShowInviteCustomerModal(true)}>
						Invite Customers
					</NavDropdown.Item> */}
					{showInviteModal && (
						<React.Suspense fallback={<></>}>
							<InviteUserModal
								close_modal={() => setShowInviteAssociateModal(false)}
								organization_id={organization_member_data?.organization?.id}
							/>
						</React.Suspense>
					)}
					{features?.switch_organization !== false &&
						(
							<>
								<NavDropdown.Item onClick={open_location_modal}>
									Change Locations
								</NavDropdown.Item>
								{showLocationModal && (
									<React.Suspense fallback={<></>}>
										<ChangeLocationModal
											close_modal={() => setShowLocationModal(false)}
										/>
									</React.Suspense>
								)}
							</>
						)}
				</>
			)}

			<NavDropdown.Divider />
			<NavDropdown.Item onClick={() => relocate("/catalog/select")}>
				Select Catalog
			</NavDropdown.Item>

			<NavDropdown.Divider />

			<NavDropdown.Item onClick={() => updateSoftware()}>
				Update Software
			</NavDropdown.Item>
			<NavDropdown.Divider />

			{features.switch_associate !== false && allowSwitchAssociate && (
				<NavDropdown.Item onClick={() => setShowChangeAssociate(true)}>
					Switch Associate
				</NavDropdown.Item>
			)}
			<NavDropdown.Item onClick={() => logout()}>
				Logout
			</NavDropdown.Item>
		</NavDropdown>
	);
}

const ManageOrgNavItem = ({ member, relocate }) => {
	const manageOrdersLabels = React.useMemo(() => ({
		administrator: 'Manage Orders',
		buyer: 'Manage Buying',
		associate: 'Manage Orders',
	}), []);
const isAdmin = member?.role?.is_administrator;
	
	/*
		If we use navigate() it does not re-trigger the Appin.js router
		and therefore is not switching to protected version.
	 */
	if (member?.organization?.id && member?.role?.label) {
		const roleLower = member.role.label.toLowerCase();
const manageOrdersLabel = manageOrdersLabels[roleLower];

		if (isAdmin || manageOrdersLabel) {
			return (
				<>
					{isAdmin &&
						<NavDropdown.Item onClick={() => relocate("/user/organization-admin")}>
							Manage
						</NavDropdown.Item>
					}
					
					{manageOrdersLabel &&
						<NavDropdown.Item onClick={() => relocate("/user/organization-admin?section=proposals")}>
							{manageOrdersLabel}
						</NavDropdown.Item>
					}
				</>
			);
		}
	}
}
